import React, { useMemo, useState, useEffect } from 'react';
import {
  Plus,
  Minus,
  Calendar,
  Sparkles,
  Hourglass,
  Wallet,
  TrendingUp,
  TrendingDown,
  CalendarClock,
  Info,
} from 'lucide-react';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';
import { formatDate, formatDateShort } from '../../utils/dateUtils';
import { buildAccountStatement } from '../../utils/leaveStatementBuilder';
import { StatusBadge } from '../UI/StatusBadge';
import PillSelect from '../UI/PillSelect';

/**
 * Bank-statement-style transactions view for the employee Leave page.
 *
 * Each leave-type "account" with a balance is presented as a savings
 * account: an account selector chip strip on top, then a statement card
 * with opening balance, total credits / debits, and a chronological
 * transactions table that ends at the current closing balance.
 *
 * Premium-but-honest: every credit and debit is traceable to an
 * underlying source (carry-forward, entitlement, accrual, OIL claim, or
 * leave application).  When the running ledger and the API balance
 * disagree, a reconciliation row is surfaced rather than silently
 * smoothing the numbers.
 */
const LeaveTransactionsView = ({
  accounts,        // array of leave balance records to expose as accounts
  applications,    // all leave applications for the selected year
  year,
  onYearChange,    // optional setter for the statement year
  user,
  loading,
  getLeaveTypeIcon,
  getLeaveTypeColorClasses,
}) => {
  const [activeAccountId, setActiveAccountId] = useState(null);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const opts = [];
    for (let y = currentYear + 1; y >= currentYear - 4; y -= 1) {
      opts.push({ value: String(y), label: String(y) });
    }
    return opts;
  }, []);

  // Auto-select the first account when accounts change.
  useEffect(() => {
    if (!accounts || accounts.length === 0) {
      if (activeAccountId !== null) setActiveAccountId(null);
      return;
    }
    const stillExists = accounts.some((a) => a.id === activeAccountId);
    if (!stillExists) setActiveAccountId(accounts[0].id);
  }, [accounts, activeAccountId]);

  const activeAccount = useMemo(
    () => accounts?.find((a) => a.id === activeAccountId) || null,
    [accounts, activeAccountId]
  );

  const statement = useMemo(() => {
    if (!activeAccount) return null;
    return buildAccountStatement({
      balance: activeAccount,
      applications: applications || [],
      year,
      user,
    });
  }, [activeAccount, applications, year, user]);

  // Empty-state when employee has no leave-balance accounts.
  if (!loading && (!accounts || accounts.length === 0)) {
    return (
      <div className="p-12 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
          <Wallet className="w-6 h-6 text-gray-400" />
        </div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          No leave accounts yet
        </h3>
        <p className="text-sm text-gray-500 max-w-sm mx-auto">
          Once you're entitled to a leave type that tracks a balance, your
          statement will appear here.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Top toolbar: statement-year picker on the right, plus instructional caption */}
      {onYearChange && (
        <div className="px-6 pt-5 pb-1 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-gray-500 max-w-md">
            A statement of every credit and deduction across your leave
            accounts. Pick an account below to see its full ledger.
          </p>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">
              Statement year
            </span>
            <PillSelect
              value={year ? String(year) : ''}
              onChange={(val) => onYearChange(val ? Number(val) : new Date().getFullYear())}
              options={yearOptions}
              theme="employee"
              minWidth="min-w-[110px]"
            />
          </div>
        </div>
      )}

      {/* Account selector strip — like switching savings accounts in a banking app */}
      <div className="px-6 pt-4 pb-2">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
          {accounts.map((account) => {
            const name = account.leave_type?.leave_type_name || 'Account';
            const isActive = account.id === activeAccountId;
            const remaining = parseFloat(
              account.remaining_days ?? account.remaining_balance ?? 0
            );
            const { icon: AccIcon, color } = getLeaveTypeIcon(name);
            const colorClasses = getLeaveTypeColorClasses(color);
            return (
              <button
                key={account.id}
                type="button"
                onClick={() => setActiveAccountId(account.id)}
                className={[
                  'group flex-shrink-0 inline-flex items-center gap-2.5 pl-2 pr-3.5 py-2 rounded-2xl border transition-all duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
                  isActive
                    ? 'border-blue-800 bg-blue-50/60 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
                ].join(' ')}
                aria-pressed={isActive}
              >
                <span
                  className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${colorClasses.bg}`}
                >
                  <AccIcon className={`w-4 h-4 ${colorClasses.text}`} />
                </span>
                <span className="flex flex-col items-start leading-tight">
                  <span className={`text-xs font-medium ${isActive ? 'text-blue-900' : 'text-gray-700'}`}>
                    {name}
                  </span>
                  <span className={`text-sm font-semibold tabular-nums ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
                    {formatLeaveDays(remaining)}
                    <span className="text-[11px] font-normal text-gray-500 ml-1">
                      {pluralizeDays(remaining)}
                    </span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Statement card */}
      {loading ? (
        <div className="p-10 text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-600 mt-3 text-sm">Loading transactions…</p>
        </div>
      ) : statement ? (
        <StatementPanel
          statement={statement}
          year={year}
          getLeaveTypeIcon={getLeaveTypeIcon}
          getLeaveTypeColorClasses={getLeaveTypeColorClasses}
        />
      ) : null}
    </div>
  );
};

const StatementPanel = ({
  statement,
  year,
  getLeaveTypeIcon,
  getLeaveTypeColorClasses,
}) => {
  const { icon: AccIcon, color } = getLeaveTypeIcon(statement.accountName);
  const colorClasses = getLeaveTypeColorClasses(color);

  // The builder orders transactions chronologically so the running balance
  // computes forward.  Employees, however, want the latest activity first —
  // exactly like a bank statement opens on your most recent transaction with
  // the opening balance at the very bottom.  Reverse for display only; each
  // row's stored balance is still the (correct) running total at that point.
  const orderedTransactions = useMemo(
    () => [...statement.transactions].reverse(),
    [statement.transactions]
  );

  return (
    <div className="px-6 pb-6">
      {/* Statement header — premium "account passbook" feel */}
      <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden">
        <div className={`p-6 sm:p-7${statement.accrualDeferred ? '' : ' border-b border-gray-200/70'}`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <span
                className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${colorClasses.bg} flex-shrink-0`}
              >
                <AccIcon className={`w-6 h-6 ${colorClasses.text}`} />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                  Leave account statement
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mt-0.5">
                  {statement.accountName}
                </h3>
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5 text-gray-400" />
                  Statement period:&nbsp;
                  <span className="font-medium text-gray-700">
                    1 Jan {year}
                  </span>
                  <span className="text-gray-400 mx-0.5">→</span>
                  <span className="font-medium text-gray-700">
                    {year === new Date().getFullYear()
                      ? 'Today'
                      : `31 Dec ${year}`}
                  </span>
                </p>
              </div>
            </div>

            {/* Closing balance — the headline number */}
            <div className="text-left sm:text-right">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                {statement.accrualDeferred ? 'Accrued so far' : 'Closing balance'}
              </p>
              <p className="text-3xl font-bold text-gray-900 tabular-nums leading-tight mt-0.5">
                {formatLeaveDays(statement.closing)}
                <span className="text-base font-medium text-gray-500 ml-1.5">
                  {pluralizeDays(statement.closing)}
                </span>
              </p>
              {statement.accrualDeferred && (
                <p className="text-xs text-blue-700 mt-1 flex items-center justify-start sm:justify-end gap-1">
                  <Hourglass className="w-3 h-3" />
                  Accruing · not yet available
                </p>
              )}
              {!statement.accrualDeferred && statement.totalPending > 0 && (
                <p className="text-xs text-orange-600 mt-1 flex items-center justify-end gap-1">
                  <Hourglass className="w-3 h-3" />
                  {formatLeaveDays(statement.totalPending)} {pluralizeDays(statement.totalPending)} pending approval
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Summary metrics row — hidden while accrual display is deferred
            (showing "0 earned" against a non-zero accrued balance would read
            as a contradiction during probation). */}
        {!statement.accrualDeferred && (
          <div className="grid grid-cols-3 divide-x divide-gray-200/70">
            <SummaryMetric
              icon={Sparkles}
              label="Brought forward"
              value={statement.opening}
              tone="neutral"
              subtitle={statement.opening > 0 ? `From ${year - 1}` : 'No carry-forward'}
            />
            <SummaryMetric
              icon={TrendingUp}
              label="Total earned"
              value={statement.totalCredits}
              tone="credit"
              subtitle={
                statement.accountName === 'Off in Lieu'
                  ? 'Approved claims credited'
                  : 'Accrual + entitlement'
              }
            />
            <SummaryMetric
              icon={TrendingDown}
              label="Total used"
              value={statement.totalDebits}
              tone="debit"
              subtitle={
                statement.accountName === 'Off in Lieu'
                  ? 'Leave drawn from this balance'
                  : 'Approved leave taken'
              }
            />
          </div>
        )}
      </div>

      {statement.accrualDeferred ? (
        <AccrualPendingPanel
          accountName={statement.accountName}
          eligibilityDate={statement.accrualEligibilityDate}
          accruedSoFar={statement.closing}
        />
      ) : (
      /* Transactions list */
      <div className="mt-5 rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-200 bg-gray-50/60 flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-gray-900 truncate min-w-0">
            Transactions
          </h4>
          <p className="text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">
            {statement.transactions.length === 0
              ? 'No activity'
              : `${statement.transactions.length} ${statement.transactions.length === 1 ? 'entry' : 'entries'}`}
          </p>
        </div>

        {statement.transactions.length === 0 ? (
          <EmptyTransactions accountName={statement.accountName} year={year} />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white">
                  <tr className="text-[11px] uppercase tracking-wider text-gray-500 font-medium border-b border-gray-200">
                    <th className="text-left px-5 py-2.5 w-[110px]">Date</th>
                    <th className="text-left px-5 py-2.5">Description</th>
                    <th className="text-right px-5 py-2.5 w-[120px]">Earned</th>
                    <th className="text-right px-5 py-2.5 w-[120px]">Used</th>
                    <th className="text-right px-5 py-2.5 w-[130px]">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orderedTransactions.map((tx) => (
                    <TransactionRow key={tx.id} tx={tx} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile / narrow: stacked cards */}
            <ul className="md:hidden divide-y divide-gray-100">
              {orderedTransactions.map((tx) => (
                <TransactionMobileRow key={tx.id} tx={tx} />
              ))}
            </ul>

            {/* Footer note */}
            <div className="px-5 py-3 bg-gray-50/60 border-t border-gray-200 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Pending applications reserve days from your balance but
                don't reduce the closing total above until they're
                approved. Approved leave is shown on its first day.
              </p>
            </div>
          </>
        )}
      </div>
      )}
    </div>
  );
};

const AccrualPendingPanel = ({ accountName, eligibilityDate, accruedSoFar }) => {
  const eligibleLabel = eligibilityDate ? formatDate(eligibilityDate) : null;
  return (
    <div className="mt-5 rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50/70 via-white to-white overflow-hidden">
      <div className="p-6 sm:p-7 flex items-start gap-4">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex-shrink-0">
          <Hourglass className="w-6 h-6" />
        </span>
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-gray-900">
            Accruing during your probation
          </h4>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            Your {accountName} is already building up in the background. Monthly
            accrual entries will start appearing in this statement once you
            complete{' '}
            <span className="font-semibold text-gray-900">3 months of service</span>
            {eligibleLabel ? (
              <>
                {' '}— on{' '}
                <span className="font-semibold text-gray-900">{eligibleLabel}</span>
              </>
            ) : (
              ''
            )}
            .
          </p>
          <p className="text-xs text-gray-500 mt-2 leading-relaxed">
            You've accrued{' '}
            <span className="font-medium text-gray-700">
              {formatLeaveDays(accruedSoFar)} {pluralizeDays(accruedSoFar)}
            </span>{' '}
            so far
            {eligibleLabel
              ? `, and you'll be able to use ${accountName} from ${eligibleLabel}.`
              : '.'}
          </p>
        </div>
      </div>
    </div>
  );
};

const SummaryMetric = ({ icon: Icon, label, value, subtitle, tone }) => {
  const toneClasses =
    tone === 'credit'
      ? 'text-emerald-600 bg-emerald-50'
      : tone === 'debit'
      ? 'text-rose-600 bg-rose-50'
      : 'text-slate-600 bg-slate-100';

  // Mobile: stack the icon ABOVE the label so 2-word labels like
  // "Brought forward" / "Total credited" never collide with the icon and
  // never bleed past the column's right edge.  At sm+ the row layout
  // returns (room is plentiful).
  return (
    <div className="px-3 py-4 sm:px-6 sm:py-5 min-w-0">
      <div className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-2 mb-2 min-w-0">
        <span
          className={`inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full ${toneClasses} flex-shrink-0`}
        >
          <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </span>
        <p className="text-[10px] sm:text-[11px] uppercase tracking-wide sm:tracking-wider text-gray-500 font-semibold leading-tight break-words min-w-0">
          {label}
        </p>
      </div>
      <p className="text-lg sm:text-xl font-semibold text-gray-900 tabular-nums leading-tight">
        {tone === 'debit' && value > 0 ? '−' : ''}
        {tone === 'credit' && value > 0 ? '+' : ''}
        {formatLeaveDays(value)}
        <span className="text-xs font-medium text-gray-500 ml-1">
          {pluralizeDays(value)}
        </span>
      </p>
      <p className="text-[10px] sm:text-[11px] text-gray-500 mt-1 leading-snug break-words">
        {subtitle}
      </p>
    </div>
  );
};

const TransactionRow = ({ tx }) => {
  const isCredit = tx.kind === 'credit' || tx.kind === 'opening';
  const isPending = tx.kind === 'pending';

  return (
    <tr className="hover:bg-gray-50/70 transition-colors duration-150">
      <td className="px-5 py-3.5 align-top">
        <div className="flex items-center gap-2">
          <KindIndicator kind={tx.kind} />
          <div className="text-sm text-gray-900 font-medium tabular-nums">
            {tx.date ? formatDateShort(tx.date) : '—'}
          </div>
        </div>
      </td>
      <td className="px-5 py-3.5 align-top">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">
              {tx.title}
            </span>
            {tx.reference && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200">
                {tx.reference}
              </span>
            )}
            {isPending && <StatusBadge status="pending" />}
          </div>
          {tx.description && (
            <p className="text-xs text-gray-500">{tx.description}</p>
          )}
        </div>
      </td>
      <td className="px-5 py-3.5 align-top text-right">
        {isCredit && tx.amount > 0 ? (
          <span className="text-sm font-semibold text-emerald-600 tabular-nums">
            +{formatLeaveDays(tx.amount)}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className="px-5 py-3.5 align-top text-right">
        {!isCredit && tx.amount > 0 ? (
          <span
            className={`text-sm font-semibold tabular-nums ${
              isPending ? 'text-orange-500' : 'text-rose-600'
            }`}
          >
            −{formatLeaveDays(tx.amount)}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className="px-5 py-3.5 align-top text-right">
        {tx.balance == null ? (
          isPending ? (
            <span className="text-xs text-gray-400 italic">reserved</span>
          ) : (
            <span className="text-gray-300">—</span>
          )
        ) : (
          <span className="text-sm font-semibold text-gray-900 tabular-nums">
            {formatLeaveDays(tx.balance)}
          </span>
        )}
      </td>
    </tr>
  );
};

const TransactionMobileRow = ({ tx }) => {
  const isCredit = tx.kind === 'credit' || tx.kind === 'opening';
  const isPending = tx.kind === 'pending';
  return (
    <li className="px-5 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <KindIndicator kind={tx.kind} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {tx.title}
            </p>
            <p className="text-xs text-gray-500 truncate">{tx.description}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-[11px] text-gray-400 inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {tx.date ? formatDate(tx.date) : '—'}
              </span>
              {tx.reference && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200">
                  {tx.reference}
                </span>
              )}
              {isPending && <StatusBadge status="pending" />}
            </div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          {isCredit ? (
            <p className="text-sm font-semibold text-emerald-600 tabular-nums">
              +{formatLeaveDays(tx.amount)}
            </p>
          ) : (
            <p
              className={`text-sm font-semibold tabular-nums ${
                isPending ? 'text-orange-500' : 'text-rose-600'
              }`}
            >
              −{formatLeaveDays(tx.amount)}
            </p>
          )}
          {tx.balance != null && (
            <p className="text-[11px] text-gray-500 tabular-nums mt-0.5">
              Bal {formatLeaveDays(tx.balance)}
            </p>
          )}
        </div>
      </div>
    </li>
  );
};

const KindIndicator = ({ kind }) => {
  // Employee mental model: earning leave adds to your balance (a "+"),
  // using leave spends it (a "−").  Plain plus / minus glyphs read far more
  // intuitively here than directional arrows on a leave statement.
  if (kind === 'credit' || kind === 'opening') {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 flex-shrink-0">
        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
    );
  }
  if (kind === 'pending') {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-50 text-orange-500 flex-shrink-0">
        <Hourglass className="w-3 h-3" />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-50 text-rose-600 flex-shrink-0">
      <Minus className="w-3.5 h-3.5" strokeWidth={2.5} />
    </span>
  );
};

const EmptyTransactions = ({ accountName, year }) => (
  <div className="p-10 text-center">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
      <Calendar className="w-5 h-5 text-gray-400" />
    </div>
    <h4 className="text-sm font-semibold text-gray-900 mb-1">
      No activity in {year}
    </h4>
    <p className="text-xs text-gray-500 max-w-xs mx-auto">
      You haven't earned or used any {accountName.toLowerCase()} this year.
    </p>
  </div>
);

export default LeaveTransactionsView;
